import React from 'react';
import { allLevels } from '../data/levels';
import type { LevelConfig } from '../data/levelSystem';
import './LevelSelector.css';

interface LevelSelectorProps {
  completedLevels: Set<string>;
  onLevelSelect: (level: LevelConfig) => void;
  onBack: () => void;
}

const LevelSelector: React.FC<LevelSelectorProps> = ({ completedLevels, onLevelSelect, onBack }) => {
  
  const isLevelUnlocked = (levelIndex: number): boolean => {
    // First level is always unlocked
    if (levelIndex === 0) return true;
    
    // Other levels unlock when previous level is completed
    const previousLevel = allLevels[levelIndex - 1];
    return completedLevels.has(previousLevel.id);
  };

  const getLevelStatus = (level: LevelConfig, index: number): 'locked' | 'available' | 'completed' => {
    if (!isLevelUnlocked(index)) return 'locked';
    if (completedLevels.has(level.id)) return 'completed';
    return 'available';
  };

  return (
    <div className="level-selector">
      <div className="level-selector-content">
        <header className="level-selector-header">
          <button className="back-btn" onClick={onBack}>
            ← Back
          </button>
          <h1 className="selector-title">Choose Your Excavation Site</h1>
          <p className="selector-subtitle">
            Each site contains fragments of ancient musical machines waiting to be restored
          </p>
        </header>

        <div className="levels-grid">
          {allLevels.map((level, index) => {
            const status = getLevelStatus(level, index);
            const isClickable = status !== 'locked';

            return (
              <div
                key={level.id}
                className={`level-card level-${status}`}
                onClick={isClickable ? () => onLevelSelect(level) : undefined}
              >
                <div className="level-card-content">
                  <div className="level-header">
                    <h3 className="level-name">{level.name}</h3>
                    <div className="level-status-indicator">
                      {status === 'completed' && <span className="status-icon">✓</span>}
                      {status === 'locked' && <span className="status-icon">🔒</span>}
                    </div>
                  </div>
                  
                  <p className="level-description">{level.description}</p>
                  
                  <div className="level-info">
                    <div className="info-item">
                      <span className="info-label">Fragments:</span>
                      <span className="info-value">{level.fragments.length}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Tempo:</span>
                      <span className="info-value">{level.tempo} BPM</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Key:</span>
                      <span className="info-value">{level.key}</span>
                    </div>
                  </div>
                  
                  {status === 'locked' && (
                    <div className="locked-message">
                      Complete previous excavation to unlock
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LevelSelector;